import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DashGisOverlay } from '../../../core/ui/overlay/dash-gis-overlay.service';
import { SearchService } from './search.service';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { ChooseSearch, ClearSearchResultFeatures, ResetSearchPagingOptions } from '../state/search.actions';
import { SearchState } from '../state/search.state';
import { Observable, Subject } from 'rxjs';
import { SearchListEntry, SearchParamModel } from '../state/search.model';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { ChooseSearchDialogComponent } from '../choose/choose-search-dialog.component';
import { SearchCriteriaDialogComponent } from '../criteria/search-criteria-dialog.component';
import { SearchResultsOverlayRef } from '../results/search-results.overlay-ref';
import { ComponentPortal } from '@angular/cdk/portal';
import { SearchResultsComponent } from '../results/search-results.component';
import { RouterNavigation } from '@ngxs/router-plugin';
import { SEARCH_RESULTS_DATA } from '../results/search-results.symbols';

@Injectable({
    providedIn: 'root',
})
export class SearchDisplayService {
    previousSearchAvailable$: Observable<boolean>;

    private currentSearch$: Observable<(a0: string) => SearchListEntry> = inject(Store).select(
        SearchState.currentSearch
    );
    private searches$: Observable<SearchListEntry[]> = inject(Store).select(SearchState.searches);
    private currentParams$: Observable<(any) => SearchParamModel> = inject(Store).select(SearchState.currentParams);
    private _previousAvailable$: Observable<(string) => boolean> = inject(Store).select(
        SearchState.previousSearchAvailable
    );

    private id: string = DashGisUtil.uid();

    constructor(
        private matDialog: MatDialog,
        private overlay: DashGisOverlay,
        private searchService: SearchService,
        private injector: Injector,
        private store: Store,
        private actions$: Actions
    ) {
        this.previousSearchAvailable$ = this._previousAvailable$.pipe(map((fn) => fn(this.id)));
    }

    showChooseDialog(finishedSignal$: Subject<void>) {
        // reset search
        this.closeResults();
        this.searchService.clearSearchParams();

        const ref = this.matDialog
            .open(ChooseSearchDialogComponent, {
                disableClose: true,
                panelClass: 'dash-gis-toolbar-dialog',
                autoFocus: false,
                data: {
                    id: this.id,
                    reset: true,
                },
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result === false) {
                    finishedSignal$.next();
                }
            });

        this.actions$
            .pipe(ofActionSuccessful(ChooseSearch), take(1))
            .subscribe(() => this.showCriteriaDialog(finishedSignal$));

        return ref;
    }

    showCriteriaDialog(finishedSignal$: Subject<void>) {
        this.hideResults();
        // this.currentSearch$.pipe(first());
        const searchDialogRef = this.matDialog.open(SearchCriteriaDialogComponent, {
            data: {
                id: this.id,
                definition$: this.currentSearch$,
            },
            disableClose: true,
            panelClass: 'dash-gis-toolbar-dialog',
            width: 'auto',
        });
        const backSub = searchDialogRef.componentInstance.back.subscribe(() => {
            searchDialogRef.close();
            this.showChooseDialog(finishedSignal$);
        });
        searchDialogRef.afterClosed().subscribe((result) => {
            backSub.unsubscribe();
            if (result) {
                const searchOptions = {
                    id: this.id,
                    params: result.params,
                    searchName: result.name,
                };
                this.showResults(searchOptions, finishedSignal$);
            }
        });
    }

    runPreviousSearch(finishedSignal$: Subject<void>) {
        this.currentSearch$.pipe(take(1), withLatestFrom(this.currentSearch$)).subscribe(([params, search]) => {
            const options = {
                id: this.id,
                params,
                searchName: search.name,
            };
            this.showResults(options, finishedSignal$);
        });
    }

    clearSearchResults() {
        setTimeout(() => this.store.dispatch(new ClearSearchResultFeatures({ displayLayer: 'searchFeatures' })), 0);
    }

    private showResults(searchOptions: any, finishedSignal$: Subject<void>) {
        const overlayRef = this.overlay.create({
            hasBackdrop: false,
            panelClass: 'dash-gis-floating-panel',
            positionStrategy: this.overlay.position().global().centerHorizontally().top('100px'),
        });
        const searchResultsRef = new SearchResultsOverlayRef(overlayRef);
        const injector = this.createInjector(searchOptions, searchResultsRef);
        const portal = new ComponentPortal(SearchResultsComponent, null, injector);
        const resultsRef = overlayRef.attach<SearchResultsComponent>(portal);
        resultsRef.instance.backToSearch.subscribe(() => {
            resultsRef.instance.close();
            this.store.dispatch(new ResetSearchPagingOptions({ id: this.id }));
            this.showCriteriaDialog(finishedSignal$);
        });
        resultsRef.instance.closeResults.subscribe(() => {
            resultsRef.instance.close();
            resultsRef.destroy();
            overlayRef.dispose();
            finishedSignal$.next();
        });

        this.actions$.pipe(ofActionSuccessful(RouterNavigation)).subscribe(() => {
            resultsRef.instance.close();
            finishedSignal$.next();
        });
    }

    private createInjector(searchOptions: Observable<any>, overlayRef: SearchResultsOverlayRef): Injector {
        return Injector.create({
            parent: this.injector,
            providers: [
                { provide: SearchResultsOverlayRef, useValue: overlayRef },
                { provide: SEARCH_RESULTS_DATA, useValue: searchOptions },
            ]
        });
    }

    private hideResults() {
        /* noop */
    }

    private closeResults() {
        /* noop */
    }
}
